import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { MessageSquare, Send, Lock, Check } from 'lucide-react';

const API_URL = window.location.origin;

const DMRequestDialog = ({ 
  isOpen, 
  onClose, 
  recipientId,
  recipientName,
  recipientAvatar
}) => {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/dm/request`,
        {
          recipient_id: recipientId,
          initial_message: message.trim() || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Message request sent to ${recipientName}`);
      onClose();
      setMessage('');
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.includes('locked')) {
        toast.error('Your DMs are locked. Engage with the community to unlock messaging.');
      } else if (detail?.includes('limit')) {
        toast.error(detail);
      } else {
        toast.error(detail || 'Failed to send request');
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-forest flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-terracotta" />
            Request to Message {recipientName}
          </DialogTitle>
        </DialogHeader>

        <div className="py-4">
          <p className="text-sm text-gray-600 mb-4">
            {recipientName} will receive your request and can choose to accept or decline. 
            This helps keep our community safe from unwanted messages.
          </p>

          <div>
            <label className="text-sm font-medium text-forest mb-2 block">
              Add a message (optional)
            </label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={`Hi ${recipientName?.split(' ')[0]}, I'd love to connect about...`}
              className="input-base min-h-[100px]"
              maxLength={500}
            />
            <p className="text-xs text-gray-400 mt-1 text-right">{message.length}/500</p>
          </div>

          <div className="mt-4 p-3 bg-forest/5 rounded-lg">
            <div className="flex items-center gap-2 text-sm text-forest">
              <Lock className="h-4 w-4" />
              <span>Your message will only be visible if they accept your request</span>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={sending}>
            Cancel
          </Button>
          <Button 
            onClick={handleSend} 
            disabled={sending}
            className="bg-terracotta hover:bg-terracotta/90 text-white"
          >
            {sending ? 'Sending...' : (
              <>
                <Send className="h-4 w-4 mr-2" /> Send Request
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// DM Request Button for profiles
export const DMRequestButton = ({ 
  recipientId, 
  recipientName,
  recipientAvatar,
  hasExistingConversation = false,
  className = ''
}) => {
  const navigate = useNavigate();
  const [showDialog, setShowDialog] = useState(false);

  // If already have conversation, go to messages
  if (hasExistingConversation) {
    return (
      <Button
        onClick={() => navigate('/messages')}
        className={`bg-forest hover:bg-forest/90 text-white ${className}`}
      >
        <MessageSquare className="h-4 w-4 mr-2" /> Message
      </Button>
    );
  }

  return (
    <>
      <Button
        onClick={() => setShowDialog(true)}
        variant="outline"
        className={`border-terracotta text-terracotta hover:bg-terracotta/10 ${className}`}
        data-testid="request-dm-btn"
      >
        <MessageSquare className="h-4 w-4 mr-2" /> Request to Message
      </Button>

      <DMRequestDialog
        isOpen={showDialog}
        onClose={() => setShowDialog(false)}
        recipientId={recipientId}
        recipientName={recipientName}
        recipientAvatar={recipientAvatar}
      />
    </>
  );
};

// Incoming DM Request Card
export const DMRequestCard = ({ request, onRespond }) => {
  const [responding, setResponding] = useState(false);

  const handleResponse = async (accept) => {
    setResponding(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/dm/requests/${request.id}/respond?accept=${accept}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (accept) {
        toast.success('Request accepted! You can now message each other.');
      } else {
        toast.info('Request declined');
      }
      
      onRespond?.(request.id, accept, response.data.conversation_id);
    } catch (error) {
      toast.error('Failed to respond to request');
    } finally {
      setResponding(false);
    }
  };

  return (
    <div className="p-4 border border-sand rounded-xl bg-white">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-full bg-terracotta/20 flex items-center justify-center text-terracotta font-medium">
          {request.sender?.full_name?.charAt(0) || '?'}
        </div>
        <div className="flex-1">
          <p className="font-medium text-forest">{request.sender?.full_name || 'Member'}</p>
          <p className="text-sm text-gray-500">wants to message you</p>
          {request.initial_message && (
            <p className="mt-2 text-sm text-gray-700 bg-sand/50 p-2 rounded-lg italic">
              &ldquo;{request.initial_message}&rdquo;
            </p>
          )}
        </div>
      </div>
      <div className="flex gap-2 mt-3">
        <Button
          size="sm"
          onClick={() => handleResponse(true)}
          disabled={responding}
          className="flex-1 bg-forest hover:bg-forest/90 text-white"
        >
          <Check className="h-4 w-4 mr-1" /> Accept
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => handleResponse(false)}
          disabled={responding}
          className="flex-1"
        >
          Decline
        </Button>
      </div>
    </div>
  );
};

export default DMRequestDialog;
